import React, { useState, useRef } from 'react';
import StudentSearchService from '../../_services/StudentSearchService';
import { parseBulkUpdateSheet, arrayBufferToBase64 } from './utils/bulkUpdateParser';
import BulkUpdatePreviewTable from './BulkUpdatePreviewTable';

interface BulkUpdateModalProps {
  show: boolean;
  onHide: () => void;
  searchParams: any;
  onSuccess: () => void;
}

type Step = 'download' | 'upload' | 'preview' | 'applying' | 'result';

const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({ show, onHide, searchParams, onSuccess }) => {
  const [step, setStep] = useState<Step>('download');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadDone, setDownloadDone] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateData, setValidateData] = useState<any>(null);
  const [applyResult, setApplyResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!show) return null;

  const handleClose = () => {
    if (step === 'result') onSuccess();
    // Reset state
    setStep('download');
    setError(null);
    setDownloading(false);
    setDownloadDone(false);
    setValidating(false);
    setValidateData(null);
    setApplyResult(null);
    onHide();
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const response = await StudentSearchService.bulkUpdateExport({ searchParams });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk_update_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setDownloadDone(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValidating(true);
    setError(null);

    try {
      const buffer = await file.arrayBuffer();

      // Parse Excel client-side
      const rows = parseBulkUpdateSheet(buffer);
      if (rows.length === 0) {
        setError('No data rows found in the uploaded file.');
        setValidating(false);
        return;
      }

      // Encode file to base64 for S3 audit
      const fileBase64 = arrayBufferToBase64(buffer);

      // Send to backend for validation + diff
      const response = await StudentSearchService.bulkUpdateValidate({
        rows,
        fileBase64,
        fileName: file.name,
      });

      if (response.data.success) {
        setValidateData(response.data.data);
        setStep('preview');
      } else {
        setError(response.data.message || 'Validation failed');
      }
    } catch (err: any) {
      const detail = err.response?.data?.message || err.message;
      setError(`Failed to process file: ${detail}`);
    } finally {
      setValidating(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleApply = async () => {
    if (!validateData) return;

    setStep('applying');
    setError(null);

    try {
      const response = await StudentSearchService.bulkUpdateApply({
        transactionId: validateData.transactionId,
        updates: validateData.resolvedData,
      });

      if (response.data.success) {
        setApplyResult(response.data.data);
        setStep('result');
      } else {
        setError(response.data.message || 'Apply failed');
        setStep('preview');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Apply failed');
      setStep('preview');
    }
  };

  const changedCount = validateData?.summary?.changedRows || 0;

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={handleClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', zIndex: 1050,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-card)', borderRadius: 8, width: '90%', maxWidth: 1000,
          maxHeight: '90vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid var(--border-default)',
        }}>
          <h5 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
            Bulk Update Enquiries via Excel
          </h5>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)', borderRadius: 4, fontSize: 13,
            }}>
              {error}
            </div>
          )}

          {/* Step: Download */}
          {step === 'download' && (
            <div>
              <div style={{ marginBottom: 16, fontSize: 14, color: '#444', lineHeight: 1.6 }}>
                <strong>Step 1:</strong> Download the current enquiry data as an Excel file.
                Edit the cells you want to update (do NOT modify the <strong>Enquiry ID</strong> or <strong>Student Name</strong> columns).
                The <strong>Reference Values</strong> sheet lists valid values for lookup fields (Centre, Counsellor, Gender).
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? 'Generating...' : 'Download Excel'}
                </button>
                {downloadDone && (
                  <span style={{ color: 'var(--status-success-text)', fontSize: 13 }}>
                    Download started. Edit the file and proceed to upload.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Step: Upload */}
          {step === 'upload' && (
            <div>
              <div style={{ marginBottom: 16, fontSize: 14, color: '#444', lineHeight: 1.6 }}>
                <strong>Step 2:</strong> Upload the modified Excel file. The system will compare your changes against the current data and show a preview.
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <label
                  className="btn btn-primary btn-sm"
                  style={{ cursor: validating ? 'wait' : 'pointer' }}
                >
                  {validating ? 'Validating...' : 'Choose Excel File'}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    disabled={validating}
                    style={{ display: 'none' }}
                  />
                </label>
                {validating && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    Parsing and validating changes...
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {step === 'preview' && validateData && (
            <div>
              <div style={{ marginBottom: 8, fontSize: 14, color: '#444' }}>
                <strong>Step 3:</strong> Review the changes below. Only rows with modifications are shown.
              </div>
              <BulkUpdatePreviewTable
                summary={validateData.summary}
                rows={validateData.rows}
              />
            </div>
          )}

          {/* Step: Applying */}
          {step === 'applying' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{
                width: 40, height: 40, border: '4px solid var(--border-default)', borderTopColor: '#4472C4',
                borderRadius: '50%', animation: 'spin 1s linear infinite',
                margin: '0 auto 16px',
              }} />
              <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>
                Applying {changedCount} update{changedCount !== 1 ? 's' : ''}...
              </p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* Step: Result */}
          {step === 'result' && applyResult && (
            <div>
              <div style={{
                padding: '16px 20px', borderRadius: 6, marginBottom: 12,
                background: applyResult.errorCount === 0 ? '#d4edda' : '#fff3cd',
              }}>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                  {applyResult.errorCount === 0
                    ? `All ${applyResult.successCount} enquiries updated successfully.`
                    : `${applyResult.successCount} updated, ${applyResult.errorCount} failed.`}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
                  Total processed: {applyResult.totalProcessed}
                </div>
              </div>
              {applyResult.errorCount > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ fontSize: 13 }}>Failed rows:</strong>
                  <ul style={{ fontSize: 13, marginTop: 4 }}>
                    {applyResult.results
                      .filter((r: any) => r.status === 'error')
                      .map((r: any, i: number) => (
                        <li key={i} style={{ color: 'var(--status-danger-text)' }}>
                          Enquiry #{r.enquiryId}: {r.error}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '12px 20px', borderTop: '1px solid var(--border-default)',
        }}>
          {step === 'download' && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={handleClose}>Cancel</button>
              {downloadDone && (
                <button className="btn btn-primary btn-sm" onClick={() => setStep('upload')}>
                  Next: Upload Modified File
                </button>
              )}
            </>
          )}
          {step === 'upload' && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setStep('download'); setError(null); }}>
              Back
            </button>
          )}
          {step === 'preview' && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => { setStep('upload'); setValidateData(null); setError(null); }}>
                Back
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleApply}
                disabled={changedCount === 0}
              >
                Apply {changedCount} Change{changedCount !== 1 ? 's' : ''}
              </button>
            </>
          )}
          {step === 'result' && (
            <button className="btn btn-primary btn-sm" onClick={handleClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateModal;
